import { useState } from 'react'
import { Button, TextField, Box, Typography, Checkbox, FormControlLabel, Stack } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import BasicModal from '@components/Modal/BasicModal'
import AlertConfirmModal from '@components/Modal/AlertConfirmModal'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useIsMobile } from '@/hooks/Responsive'
import { getMyCourseCnt, deleteMember } from "@api/myPageAccount";
import { useAuth } from '@components/AuthContext';
import { useConfirm } from '@components/ConfirmProvider'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'

function InfoRow({ label, children, isMobile }) {
  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      alignItems={isMobile ? 'flex-start' : 'flex-start'}
      sx={{
        mb: 3,
        gap: isMobile ? 1 : 5,
      }}
    >
      <Box sx={{ width: isMobile ? '100%' : '160px' }}>
        <Typography fontWeight={500}>
          {label}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, width: isMobile ? '100%' : 'auto' }}>
        {typeof children === 'string' ? (
          <Typography>
            {children}
          </Typography>
        ) : (
          children
        )}
      </Box>
    </Stack>
  )
}

export default function WithdrawModal({
  open,
  onClose,
  email,
  org,
  id,
}) {
  const { isMobile } = useIsMobile()
  const lang = useLang() 
  const navigate = useNavigate()
  const { alert, confirm } = useConfirm();

  const [reason, setReason] = useState('')
  const [reasonError, setReasonError] = useState(false)

  const [checked, setChecked] = useState(false)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [doneOpen, setDoneOpen] = useState(false)    

  const { logout } = useAuth();

  // 리셋폼
  const resetForm = () => {
    setReason('')
    setChecked(false)
    setConfirmOpen(false)
    setDoneOpen(false)
  }
  
  // 회원탈퇴 버튼
  const handleWithdrawClick = async () => {
    // 탈퇴사유 체크
    const trimReason = reason.trim()
    if (!trimReason) {
      setReasonError(true)
      return
    }

    try {
      const params = {
        publicId: id,
      };

      const response = await getMyCourseCnt(params);

      if (response?.success) {
        setReasonError(false)
        setConfirmOpen(true)
      }
      else {
        handleConfirmClose();
        resetForm();
        onClose();
        alert(response?.message || "조회 중 오류가 발생했습니다.");
      }
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || e.message || "조회 중 오류가 발생했습니다.");
    }
  }

  const handleConfirmClose = () => {
    setConfirmOpen(false)
  }

  // 정말 탈퇴하시겠습니까? => 확인
  const handleConfirmAccept = async () => {

    try {
      const params = {
        publicId: id,
        leaveReason: reason,
      };

      const response = await deleteMember(params);

      if (response?.success) {
        setConfirmOpen(false)
        setDoneOpen(true)
      }
      else {
        alert(response?.message || "삭제 중 오류가 발생했습니다.");
      }
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || e.message || "삭제 중 오류가 발생했습니다.");
    }
  }

  // 탈퇴 완료
  const handleDoneClose = async () => {
    try {
      setDoneOpen(false)
      await logout();  // 서버 /api/auth/logout 호출 + 상태 초기화
    } finally {
      navigate(buildLangHref('/', lang));
    }
  }

  return (
    <BasicModal
      open={open}
      onClose={onClose}
      onReset={resetForm}
      title="회원탈퇴"
      actions={
        <>
          <Button
            variant="outlined"
            color="grey"
            onClick={handleWithdrawClick}
            disabled={!checked}
          >
            회원탈퇴
          </Button>
        </>
      }
    >
      {/* 안내 박스 */}
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: 'grey.50',
          mb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
          <InfoOutlinedIcon fontSize="small" /> 
          <Typography fontWeight={600}>
            회원 탈퇴 시, 이메일 계정 재사용 및 복구가 불가합니다.
          </Typography>
        </Stack>
        <Typography sx={{ mb: 0.5 }}>
          회원 탈퇴를 신청하시면 현재 접속 중인 이메일 계정은 즉시 탈퇴 처리됩니다.<br />
          삭제되는 항목은 다음과 같습니다.
        </Typography>
        <Stack
          component="ul"
          sx={{
            p: 0,
            m: 0,
            listStyle: 'none',
            '& li': {
              position: 'relative',
              paddingLeft: '1em',
              typography: 'body2'
            },
            '& li::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '8px',
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              bgcolor: 'grey.600',
            },
          }}
        >
          <Typography component="li">회원정보: 아이디/비밀번호/생년월일/이메일/성별/지역 등 모든 개인정보</Typography>
          <Typography component="li">강좌 및 콘텐츠 선호도 정보</Typography>
          <Typography component="li">내가 남긴 게시글</Typography>
          <Typography component="li">기타: 접속 IP, 접속기록 등 접속로그 데이터</Typography>
        </Stack>
      </Box>

      {/* 회원정보 */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 3 }}>
        회원정보
      </Typography>

      {/* 이메일 */}
      <InfoRow label="이메일 계정" isMobile={isMobile}>
        {email}
      </InfoRow>

      {/* 기관 */}
      <InfoRow label="계정 연동 기관" isMobile={isMobile}>
        {org || '기관 없음'}
      </InfoRow>

      {/* 탈퇴 사유 */}
      <InfoRow label="탈퇴사유" isMobile={isMobile}>
        <TextField
          fullWidth
          multiline
          minRows={4}
          placeholder="탈퇴 사유를 입력해 주세요"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value)
            if (reasonError) setReasonError(false)
          }}
          inputProps={{ maxLength: 200 }}
          error={reasonError}
          helperText={reasonError ? '탈퇴 사유를 입력해 주세요.' : ''}
        />
      </InfoRow>

      {/* checkbox */}
      <FormControlLabel
        sx={{ alignItems: 'flex-start' }}
        control={
          <Checkbox
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            sx={{ pt: 0 }}
          />
        }
        label={
          <Typography fontWeight={500}>
            안내 사항을 모두 확인했으며, 탈퇴 시 이메일 계정 재사용 및 데이터 복구가 불가능한 점에 동의합니다.
          </Typography>
        }
      />
      <Typography 
        variant="body2" 
        sx={{ 
          position: 'relative',
          mt: 1,
          mb: 6,
          pl: '1em',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '8px',
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            bgcolor: 'grey.600',
          },
        }}
      >
        기관 학습자의 회원 탈퇴는 소속 기관 관리자의 확인 후 완료됩니다.
      </Typography>
      <AlertConfirmModal
        open={confirmOpen}
        onClose={handleConfirmClose}
        onCancel={handleConfirmClose}
        title="정말 탈퇴하시겠습니까?"
        description={`탈퇴 시 개인정보가 모두 파기됩니다.\n계속 진행하시겠습니까?`}
        type="confirm"
        onConfirm={handleConfirmAccept}
      />

      {/* 두 번째 - 완료 알럿 */}
      <AlertConfirmModal
        open={doneOpen}
        onClose={handleDoneClose}
        title="탈퇴 완료"
        description={`회원탈퇴가 완료되었습니다.`}
        type="alert"
        onConfirm={handleDoneClose}
      />
    </BasicModal>
  )
}
